// frontend/src/components/portfolio/SEOSettingsPanel.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  TextField,
  Typography,
  IconButton,
  Button,
  Divider,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  Language as LanguageIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import MediaPicker from './MediaPicker';

interface SEOSettingsPanelProps {
  open: boolean;
  onClose: () => void;
  portfolio: {
    title: string;
    description?: string;
    meta: {
      ogTitle?: string;
      ogDescription?: string;
      ogImage?: string;
      favicon?: string;
    };
  };
  onUpdate: (meta: {
    ogTitle?: string;
    ogDescription?: string;
    ogImage?: string;
    favicon?: string;
  }) => void;
}

const SEOSettingsPanel: React.FC<SEOSettingsPanelProps> = ({
  open,
  onClose,
  portfolio,
  onUpdate
}) => {
  const [ogTitle, setOgTitle] = useState('');
  const [ogDescription, setOgDescription] = useState('');
  const [ogImage, setOgImage] = useState('');
  const [favicon, setFavicon] = useState('');
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [mediaPickerMode, setMediaPickerMode] = useState<'ogImage' | 'favicon'>('ogImage');

  // Initialize from portfolio
  useEffect(() => {
    if (portfolio) {
      setOgTitle(portfolio.meta.ogTitle || portfolio.title || '');
      setOgDescription(portfolio.meta.ogDescription || portfolio.description || '');
      setOgImage(portfolio.meta.ogImage || '');
      setFavicon(portfolio.meta.favicon || '');
    }
  }, [portfolio, open]);

  // Auto-save on change (debounced)
  useEffect(() => {
    if (!open) return;

    const timer = setTimeout(() => {
      onUpdate({
        ogTitle: ogTitle || undefined,
        ogDescription: ogDescription || undefined,
        ogImage: ogImage || undefined,
        favicon: favicon || undefined
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, [ogTitle, ogDescription, ogImage, favicon, open]);

  const handleMediaSelect = (imageUrl: string) => {
    if (mediaPickerMode === 'ogImage') {
      setOgImage(imageUrl);
    } else {
      setFavicon(imageUrl);
    }
    setMediaPickerOpen(false);
  };

  const openOgImagePicker = () => {
    setMediaPickerMode('ogImage');
    setMediaPickerOpen(true);
  };

  const openFaviconPicker = () => {
    setMediaPickerMode('favicon');
    setMediaPickerOpen(true);
  };

  const clearOgImage = () => setOgImage('');
  const clearFavicon = () => setFavicon('');

  const ogTitleLength = ogTitle.length;
  const ogDescriptionLength = ogDescription.length;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
  <DialogTitle
  sx={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #444',  // ← darker border
    bgcolor: '#2C3E50',               // ← match your header
    color: 'white'                    // ← white text
  }}
>
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <LanguageIcon sx={{ color: 'white' }} /> 
    <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
      SEO & Social Sharing Settings
    </Typography>
  </Box>
  <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
    <CloseIcon />
  </IconButton>
</DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            These settings control how your portfolio appears when shared on social media (Facebook, Twitter, LinkedIn) and in search results.
          </Alert>

          {/* OG Title */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Social Share Title
            </Typography>
            <TextField
              fullWidth
              value={ogTitle}
              onChange={(e) => setOgTitle(e.target.value)}
              placeholder={portfolio.title}
              helperText={
                ogTitleLength > 60
                  ? `${ogTitleLength}/60 characters - May be truncated on social media`
                  : `${ogTitleLength}/60 characters`
              }
              FormHelperTextProps={{
                sx: { color: ogTitleLength > 60 ? 'warning.main' : 'text.secondary' }
              }}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* OG Description */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Social Share Description
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={ogDescription}
              onChange={(e) => setOgDescription(e.target.value)}
              placeholder={portfolio.description || 'Brief description of your portfolio...'}
              helperText={
                ogDescriptionLength > 160
                  ? `${ogDescriptionLength}/160 characters - May be truncated`
                  : `${ogDescriptionLength}/160 characters`
              }
              FormHelperTextProps={{
                sx: { color: ogDescriptionLength > 160 ? 'warning.main' : 'text.secondary' }
              }}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* OG Image */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Social Share Image
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This image appears when your portfolio is shared. Recommended: 1200x630px
            </Typography>
            
            {ogImage ? (
              <Box sx={{ position: 'relative', mb: 2 }}>
                <Box
                  component="img"
                  src={ogImage}
                  alt="OG Image Preview"
                  sx={{
                    width: '100%',
                    maxHeight: 300,
                    objectFit: 'cover',
                    borderRadius: 1,
                    border: '1px solid #e0e0e0'
                  }}
                />
                <IconButton
                  onClick={clearOgImage}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'rgba(0, 0, 0, 0.6)',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.8)'
                    }
                  }}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: 200,
                  border: '2px dashed #ccc',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2
                }}
              >
                <Typography color="text.secondary">No image selected</Typography>
              </Box>
            )}

            <Button
              variant="outlined"
              startIcon={<ImageIcon />}
              onClick={openOgImagePicker}
              fullWidth
            >
              {ogImage ? 'Change Image' : 'Select Image'}
            </Button>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Favicon */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Favicon
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Small icon that appears in browser tabs. Recommended: Square image (512x512px)
            </Typography>
            
            {favicon ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box
                  component="img"
                  src={favicon}
                  alt="Favicon Preview"
                  sx={{
                    width: 64,
                    height: 64,
                    objectFit: 'cover',
                    borderRadius: 1,
                    border: '1px solid #e0e0e0'
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Current Favicon
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {favicon.split('/').pop()}
                  </Typography>
                </Box>
                <IconButton onClick={clearFavicon} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            ) : (
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  border: '2px dashed #ccc',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2
                }}
              >
                <ImageIcon sx={{ color: 'text.secondary' }} />
              </Box>
            )}

            <Button
              variant="outlined"
              startIcon={<ImageIcon />}
              onClick={openFaviconPicker}
              fullWidth
            >
              {favicon ? 'Change Favicon' : 'Select Favicon'}
            </Button>
          </Box>

          {/* Auto-save indicator */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 3, textAlign: 'center' }}
          >
            Changes are saved automatically
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Media Picker Modal */}
      <MediaPicker
        open={mediaPickerOpen}
        onClose={() => setMediaPickerOpen(false)}
        onSelect={handleMediaSelect}
      />
    </>
  );
};

export default SEOSettingsPanel;